"use client";

// B3.2 MARKETPLACE BROWSE — WO-GF-001 Phase-1b, real machinery for the marketplace browse system.
// CONTENT-REGION REPLACEMENT (inverts the B1 chrome strip): the mirror hub-header, cart and footer
// are kept as faithful mirror subtrees (B2 chrome stays MIRRORED per the B2 ruling); the MAIN region
// (leftDock filters + centercss supplier directory) is rebuilt as this real component.
//
// What is REAL here (the port, vs the dead mirror):
//   • the CHOOSE PRODUCT TYPE checkboxes actually filter the visible supplier cards (dead in mirror
//     = R6-6 photocopy behavior; live here — proven by the behavior run).
//   • the 5 live-matching suppliers are wired to the BE marketplace (/marketplace/listings): the
//     server marks them data-be-backed from the live BE response; fictional BE seed vendors are not
//     in the live directory and never render (live = truth).
// Card presentation is served from the faithful captured markup (absolute S3 logo urls → pixel
// parity); it re-skins from @repo/tokens at the Phase-2 pass (PH2-RESKIN). Suppliers without BE data
// stay faithful mirror cards + F29. Desktop only in B3.2; mobile stays fully mirrored (F30).

import { useEffect, useMemo, useRef, useState } from "react";
import MarketplaceCartLayer from "./MarketplaceCartLayer";

export type BrowseCard = {
  slug: string;
  name: string;
  types: string[];
  beVendor: string | null;
  html: string;
};

export type BrowseData = {
  slug: string;
  variant: string;
  heading: string;
  productTypes: { id: string; label: string; token: string }[];
  chrome: {
    headerClass: string;
    headerInner: string;
    cartClass: string;
    cartInner: string;
    footerClass: string;
    footerInner: string;
  };
  leftDockInner: string;
  cards: BrowseCard[];
};

// Faithful reproduction of the live main region's inline nprogress style (hidden progress bar).
const NPROGRESS_CSS = `
    #nprogress { pointer-events: none; }
    #nprogress .bar { background: #29D; position: fixed; z-index: 9999; top: 0; left: 0; width: 100%; height: 3px; }
    #nprogress .peg { display: block; position: absolute; right: 0px; width: 100px; height: 100%; box-shadow: 0 0 10px #29D, 0 0 5px #29D; opacity: 1; transform: rotate(3deg) translate(0px, -4px); }
    #nprogress .spinner { display: block; position: fixed; z-index: 1031; top: 15px; right: 15px; }
    #nprogress .spinner-icon { width: 18px; height: 18px; box-sizing: border-box; border: solid 2px transparent; border-top-color: #29D; border-left-color: #29D; border-radius: 50%; animation: nprogress-spinner 400ms linear infinite; }
    @keyframes nprogress-spinner { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  `;

export default function MarketplaceBrowse({
  data,
  liveVendorNames,
}: {
  data: BrowseData;
  liveVendorNames: string[];
}) {
  const { chrome } = data;

  // map a checkbox's visible label (e.g. "SNOWMOBILE") → product-type token
  const labelToToken = useMemo(() => {
    const m = new Map<string, string>();
    for (const pt of data.productTypes) m.set(pt.label.toUpperCase(), pt.token);
    return m;
  }, [data.productTypes]);

  const liveSet = useMemo(() => new Set(liveVendorNames), [liveVendorNames]);

  // active product-type filter (empty = show all). The CHOOSE PRODUCT TYPE checkboxes drive this.
  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set());

  // The leftDock filter markup is faithful mirror HTML (dangerouslySetInnerHTML), so React's
  // synthetic onChange does NOT track its raw checkboxes. Attach a NATIVE change listener instead:
  // native change events bubble from the checkboxes up to this container reliably.
  const leftDockRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = leftDockRef.current;
    if (!node) return;
    const onChange = (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      if (!target || target.type !== "checkbox") return;
      const labelEl = target.closest(".form-check")?.querySelector(".form-check-label");
      const token = labelEl ? labelToToken.get((labelEl.textContent || "").trim().toUpperCase()) : undefined;
      if (!token) return;
      setActiveTypes((prev) => {
        const next = new Set(prev);
        if (target.checked) next.add(token);
        else next.delete(token);
        return next;
      });
    };
    node.addEventListener("change", onChange);
    return () => node.removeEventListener("change", onChange);
  }, [labelToToken]);

  // Memoize the leftDock element so its reference is STABLE across filter re-renders. React bails
  // out of reconciling an identical element, so it never re-applies dangerouslySetInnerHTML — the
  // raw checkboxes keep their DOM checked-state (otherwise every filter would reset them, breaking
  // un-checking). The native listener above stays attached to this same node.
  const leftDockEl = useMemo(
    () => (
      <div
        className="leftDock"
        ref={leftDockRef}
        dangerouslySetInnerHTML={{ __html: data.leftDockInner }}
      />
    ),
    [data.leftDockInner],
  );

  const isVisible = (card: BrowseCard) =>
    activeTypes.size === 0 || card.types.some((t) => activeTypes.has(t));

  return (
    <div id="__next">
      <div className="__className_d275c8 mobileWidthSet">
        {/* MIRROR chrome — faithful hub header (sticky), cart, footer. B2 chrome stays MIRRORED. */}
        <header
          className={chrome.headerClass}
          dangerouslySetInnerHTML={{ __html: chrome.headerInner }}
        />
        <section
          className={chrome.cartClass}
          dangerouslySetInnerHTML={{ __html: chrome.cartInner }}
        />

        {/* REAL main region (css-1phsixa) — filters + supplier directory as live machinery. */}
        <div className="MuiBox-root css-1phsixa">
          <style dangerouslySetInnerHTML={{ __html: NPROGRESS_CSS }} />
          <div>
            <div className="d-flex mt-4 mb-5 ThreeColumns flexStart ThreeLayout_flexStart__h8cIe">
              {/* leftDock: faithful filter markup, but its checkboxes are now LIVE via delegation. */}
              {leftDockEl}
              <div className="px-4 centercss">
                <h1 className="mainHeading">{data.heading}</h1>
                {data.cards.map((card) => {
                  const beBacked = !!card.beVendor && liveSet.has(card.beVendor);
                  const visible = isVisible(card);
                  return (
                    <div
                      key={card.slug}
                      className="mb-3"
                      data-supplier={card.slug}
                      data-producttypes={card.types.join(" ")}
                      data-be-backed={beBacked ? "true" : "false"}
                      style={visible ? undefined : { display: "none" }}
                      dangerouslySetInnerHTML={{ __html: card.html }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <footer
          className={chrome.footerClass || undefined}
          dangerouslySetInnerHTML={{ __html: chrome.footerInner }}
        />
      </div>
      {/* F36 — real cart badge + tray, coherent with the detail pages (store persists across nav). */}
      <MarketplaceCartLayer />
    </div>
  );
}
